/* ==========================================================================
   WAYPOINT — the screen-level error boundary.

   Why this exists: React unmounts the WHOLE tree when a render or an effect
   throws and nothing catches it. With no boundary anywhere above the routes,
   a single bad render didn't degrade — it painted the app white, with no
   message, no nav, and no way back except a manual reload. That is exactly
   what "it opens to a blank page" looks like from the outside, and it hides
   the one thing that would explain it: the error text.

   So every screen renders inside one of these. A crash now costs you that
   screen, not the app: you get what broke, a Reload, and a way back to
   Trips. It sits INSIDE <Screen> (see App.tsx), so the bottom nav stays lit
   and the boundary resets on its own when the path changes — a crash on one
   trip never sticks to the next one.

   Errors thrown outside React's render/effect cycle (event handlers, async
   callbacks) never reach a boundary, so the global listeners below record
   the most recent one and the panel shows it as extra detail when it lands. */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { navigate } from '../lib/router';

/** Last uncaught error seen outside React, if any — see the header note. */
let lastGlobalError: string | null = null;

if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
    lastGlobalError = e.message || String(e.error ?? '');
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason as unknown;
    lastGlobalError = reason instanceof Error ? reason.message : String(reason);
  });
}

/** True when one message is the other, or contains it — see the render below. */
function overlaps(a: string, b: string): boolean {
  return a.includes(b) || b.includes(a);
}

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  message: string | null;
  detail: string | null;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { message: null, detail: null };

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    const message = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
    return { message, detail: lastGlobalError };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    // Console, not a service — this app has two users and no telemetry. The
    // stack is the useful half when either of them opens the inspector.
    console.error('[waypoint] screen crashed', error, info.componentStack);
  }

  render() {
    if (this.state.message === null) return this.props.children;
    return (
      <div className="wp-crash" role="alert">
        <h2 className="text-title">This screen hit a snag</h2>
        <p className="text-body wp-crash__copy">
          Waypoint caught the error instead of going blank. Reload to try again — and if it keeps
          happening, the line below is the bit worth screenshotting.
        </p>
        <pre className="wp-crash__detail">{this.state.message}</pre>
        {/* Only when it's genuinely a SECOND fact. The same throw usually reaches
            both the boundary and the global listener, worded slightly differently
            ("Error: x" vs "Uncaught Error: x"), and printing it twice reads as two
            separate failures. */}
        {this.state.detail && !overlaps(this.state.detail, this.state.message) && (
          <pre className="wp-crash__detail">{this.state.detail}</pre>
        )}
        <div className="wp-crash__actions">
          <button
            type="button"
            className="wp-crash__button wp-crash__button--ghost"
            onClick={() => {
              this.setState({ message: null, detail: null });
              navigate('/');
            }}
          >
            Back to trips
          </button>
          <button type="button" className="wp-crash__button" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
        <style>{`
          .wp-crash {
            padding: calc(76px + var(--sat)) 24px 40px;
            max-width: 520px;
            margin: 0 auto;
            text-align: center;
          }
          .wp-crash__copy { color: var(--ink-soft); margin-top: 8px; }
          .wp-crash__detail {
            margin-top: 16px;
            padding: 12px 14px;
            border-radius: 14px;
            background: var(--canvas-raised);
            border: 1.5px solid var(--cloud);
            color: var(--ink-soft);
            font-size: 13px;
            line-height: 1.4;
            text-align: left;
            white-space: pre-wrap;
            word-break: break-word;
          }
          .wp-crash__actions { display: flex; gap: 10px; margin-top: 20px; }
          .wp-crash__button {
            flex: 1;
            min-height: 50px;
            border-radius: var(--radius-pill);
            background: var(--ember);
            color: var(--canvas-raised);
            font-weight: 700;
          }
          .wp-crash__button--ghost {
            background: var(--canvas-raised);
            border: 1.5px solid var(--cloud);
            color: var(--ink-soft);
          }
        `}</style>
      </div>
    );
  }
}
